/* eslint-disable indent */
/* eslint-disable eqeqeq */
import {html, LitElement} from 'lit';
import styles from './g-tabs.component.scss?as=stylesheet';
import {defineCustomElement} from 'extensions/utils';

/**
 *	<g-tabs active="my-id">
 *		<g-tab label="My Tab" controls="my-id" ?open>
 *		<g-tab-panel id="my-id" ?open>
 *			html here...
 *		</g-tabs-panel>
 *	</g-tabs>
 */

/**
 * @param {any[]} arr
 * @returns {any[]}
 */
const loopable = (input = []) => {
	const handler = {
		get (arr, prop) {
			const parsed = parseInt(prop, 10);
			if (Number.isInteger(parsed)) {
				if (parsed < 0) {
					return arr[arr.length - 1];
				}
				if (parsed >= arr.length) {
					return arr[0];
				}
			}
			return arr[prop];
		}
	};

	return new Proxy(input, handler);
};


class GenesisTabs extends LitElement {
	static styles = [styles];

	static get properties () {
		return {
			// The active tab id
			active: {
				type: String
			},
			label: {
				type: String
			}
		};
	}

	constructor () {
		super();

		this.label = '';

		this.handleTabOpen = this._handleTabOpen.bind(this);
		this.handleTabKeyup = this._handleTabKeyup.bind(this);

		this.setAttribute('role', 'tablist');
	}

	connectedCallback () {
		super.connectedCallback();

		this.addEventListener('tab:open', this.handleTabOpen);
		this.addEventListener('tab:keyup', this.handleTabKeyup);
	}

	/**
	 * Getter that returns an array of children in the "tab" slot.
	 */
	get tabChildren () {
		return loopable(this.shadowRoot
			?.querySelector('slot[name="tab"]')
			?.assignedElements({flatten: true})
			?.filter(childNode => childNode.nodeName === 'G-TAB'));
	}

	/**
	 * Getter that returns an array of children in the "panel" slot.
	 */
	get panelChildren () {
		return this.shadowRoot
			?.querySelector('slot[name="panel"]')
			?.assignedElements({flatten: true})
			?.filter(childNode => childNode.nodeName === 'G-TAB-PANEL');
	}

	/**
	 * Catch bubbled `tab:open` event and open/close tabs and panels based on logic.
	 */
	_handleTabOpen (e) {
		// eslint-disable-next-line array-element-newline
		for (let child of [this.tabChildren, this.panelChildren].flat()) {
			child.open = child.controls == e.detail?.controls || child.id == e.detail?.controls;
		}
	}

	/**
	 * Catch bubbled `tab:keyup` event and goto next or previous tabs in order
	 */
	_handleTabKeyup ({detail}) {
		switch (detail.direction) {
			case 'left': {
				const tab = this.tabChildren[this.activeIndex - 1] || {};

				this.active = tab.controls;
				tab?.focus?.();
				break;
			}
			case 'right': {
				const tab = this.tabChildren[this.activeIndex + 1] || {};

				this.active = tab.controls;
				tab?.focus?.();
				break;
			}
			default: {
				break;
			}
		}
	}

	/**
	 * Fire when a slot has changed. Used only once. Sets the first tab/panel to be open on initialization.
	 */
	handeSlotChange (e) {
		const childNodes = e.target
			?.assignedNodes({flatten: true})
			?.filter(childNode => childNode.nodeName.startsWith('G-TAB'));

		// eslint-disable-next-line array-element-newline
		for (let [index, childNode] of childNodes.entries()) {
			if (index === 0) {
				childNode.open = true;
			}
		}

		this.requestUpdate();
	}

	handleMobileTabsChange (e) {
		const [target] = e.composedPath();

		// Set active tab to whatever the select menu is
		this.active = target.value;
	}

	get activeIndex () {
		return this.tabChildren?.findIndex(tab => tab.open) || 0;
	}

	get active () {
		return (this.tabChildren?.find(tab => tab.open) || {}).id;
	}

	set active (id) {
		// Dispatch event to downstream components
		this.dispatchEvent(new CustomEvent('tab:open', {
			detail: {
				controls: id
			}
		}));

		// Manually fire request update
		this.requestUpdate('active', this.active);

		return id;
	}

	render () {
		return html`
			<div class="container">
				<div class="tabs" role="tablist" aria-label="${this.label}">
					<slot
						name="tab"
						@slotchange="${{
							handleEvent: e => this.handeSlotChange(e),
							once: true
						}}">
					</slot>
				</div>
				<div class="mobile-tabs">
					<div class="mobile-tabs-container">
						<label id="mobile-tab-control" class="mobile-tabs-label">Shop For</label>
						<select id="mobile-tab-control" class="mobile-tabs-control" @change="${this.handleMobileTabsChange}">
							${this.tabChildren?.map(tab => html`<option .value="${tab.controls}">${tab.label}</option>`)}
						</select>
					</div>
				</div>
				<div class="panels">
					<slot
						name="panel"
						@slotchange="${{
							handleEvent: e => this.handeSlotChange(e),
							once: true
						}}">
					</slot>
				</div>
			</div>
		`;
	}
};

defineCustomElement('g-tabs', GenesisTabs);
